import { keyframes, ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { Button } from '@ui/core/button/Button';
import { theme } from '@ui/theme/cobrainer-theme';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { GlobalSearchBarV2 } from './GlobalSearchBarV2';
import { useNavigationMenu } from './NavigationMenuContext';
import { useEscapeKeyClose, useSideDrawerLinks } from './use-side-drawer-links';
import { UserMenuV2 } from './UserMenuV2';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import { useGlobalPermissions } from '../../query-hooks/global-permissions';
import { CustomThemeProvider } from '../../theming';
import { VisibilityIndicator } from '../VisibilityIndicator';

import type {
    SideDrawerLinkProperties,
    SideDrawerListItem,
    SideDrawerParentLinkProperties,
} from '../navigation-menu-list-item';
import type { FunctionComponent } from 'react';

const fadeIn = keyframes({
    from: {
        opacity: 0,
    },
    to: {
        opacity: 1,
    },
});

const DrawerContainer = styled.div<{ readonly isOpen: boolean }>(({ isOpen, theme }) => ({
    animation: `${fadeIn} 500ms ease-in`,
    backgroundColor: theme.colors.gray[200],
    height: 'calc(100vh - 60px)',
    padding: `${theme.spacing.m} ${theme.spacing.s}`,
    paddingTop: theme.spacing.s,
    position: 'sticky',
    top: '54px',
    transition: 'transform 0.3s ease-in-out',
    width: isOpen ? '250px' : 'auto',
}));

export const DrawerHeader = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing['4xl'],
    paddingLeft: 0,
}));

const DrawerContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xl,
});

const MenuContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: theme.spacing.m,
    justifyContent: 'space-between',
}));

const RightSection = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
}));

const isSideDrawerParentLink = (link: SideDrawerListItem): link is SideDrawerParentLinkProperties => {
    return 'children' in link && is.array(link.children);
};

const ListItemButton = styled(Button)<{ readonly isOpen: boolean }>(({ isOpen = false, theme }) => ({
    justifyContent: 'flex-start',
    padding: `calc(${theme.spacing.m} + ${theme.spacing.xxs})`,
    textAlign: 'left',
    width: isOpen ? '100%' : 'auto',
}));

const ListItemIcon = styled.div<{ readonly isActive: boolean }>(({ isActive, theme }) => ({
    alignItems: 'center',
    color: theme.colors.gray[600],
    display: 'flex',
    ...(isActive
        ? {
              color: theme.colors.orange[800],
          }
        : {}),
}));

const ListItemText = styled(motion.span)<{ readonly isActive: boolean }>(({ isActive, theme }) => ({
    color: theme.colors.black[800],
    ...theme.typography.body.S.medium,
    flex: 1,
    lineHeight: 0,
    paddingLeft: theme.spacing.m,
    ...(isActive
        ? {
              color: theme.colors.orange[800],
          }
        : {}),
}));

export const SideDrawerLinkV2: FunctionComponent<SideDrawerLinkProperties> = (props) => {
    const { isOpen } = useNavigationMenu();
    const {
        actions: { navigateTo },
    } = useContext(ApplicationContext);
    const { externalUrl, label, NewIcon, onRouteChange, path } = props;
    const isActive = useLocation().pathname.includes(path);
    const hasAnimatedRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            hasAnimatedRef.current = true;
        }
    }, [isOpen]);

    return (
        <ListItemButton
            iconOnly={!isOpen}
            isOpen={isOpen}
            variant="ghost"
            onClick={() => {
                if (externalUrl) {
                    window.open(path, '_blank');
                } else {
                    navigateTo(path);
                }
                onRouteChange?.();
            }}
        >
            <ListItemIcon isActive={isActive}>
                <NewIcon size={theme.typography.body.M.regular.fontSize} />
            </ListItemIcon>
            <AnimatePresence>
                {isOpen && (
                    <ListItemText
                        animate={{ opacity: 1, transform: 'translateX(0)' }}
                        initial={hasAnimatedRef.current ? { opacity: 0.5, transform: 'translateX(-10px)' } : false}
                        isActive={isActive}
                    >
                        {label}
                    </ListItemText>
                )}
            </AnimatePresence>
        </ListItemButton>
    );
};

type SideDrawerLinkListProps = {
    readonly links: readonly SideDrawerListItem[];
};

export const SideDrawerLinkListV2: FunctionComponent<SideDrawerLinkListProps> = ({ links }) => {
    return (
        <Fragment>
            {links.map((link) => {
                if (isSideDrawerParentLink(link)) {
                    return (
                        <Fragment key={link.label}>
                            <SideDrawerLinkListV2 links={link.children} />
                        </Fragment>
                    );
                }

                return (
                    <SideDrawerLinkV2
                        key={link.label}
                        externalUrl={link.externalUrl}
                        Icon={link.Icon}
                        label={link.label}
                        NewIcon={link.NewIcon}
                        path={link.path}
                        onRouteChange={link.onRouteChange}
                    />
                );
            })}
        </Fragment>
    );
};

export const SideDrawerNavigationV2: FunctionComponent = () => {
    const { closeMenu, isOpen: isDrawerOpen } = useNavigationMenu();
    const { translate } = useContext(ApplicationContext);
    const sideDrawerLinksConfig = useSideDrawerLinks();

    useEscapeKeyClose(isDrawerOpen, closeMenu);

    return (
        <ThemeProvider theme={theme}>
            <DrawerContainer aria-label={translate('side-navigation.aria-label')} isOpen={isDrawerOpen}>
                <DrawerContent>
                    <SideDrawerLinkListV2 links={sideDrawerLinksConfig} />
                </DrawerContent>
            </DrawerContainer>
        </ThemeProvider>
    );
};

const CenteredWrapper = styled.div({
    '@media (min-width: 1600px)': {
        left: '50%',
        // maxWidth: '500px',
        minWidth: '500px',
        position: 'absolute',
        transform: 'translateX(-50%)',
    },
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
});

export const NavigationMenuV2: FunctionComponent = () => {
    const { data: globalPermissions, isSuccess } = useGlobalPermissions();

    return (
        <ThemeProvider theme={theme}>
            <MenuContainer>
                <div />
                <CenteredWrapper>
                    {isSuccess && globalPermissions.canUserSearch && <GlobalSearchBarV2 />}
                </CenteredWrapper>
                <RightSection>
                    <CustomThemeProvider>
                        <VisibilityIndicator />
                    </CustomThemeProvider>
                    <UserMenuV2 />
                </RightSection>
            </MenuContainer>
        </ThemeProvider>
    );
};
